/* eslint-disable react-hooks/refs -- Phase 1 reconciliation intentionally reads refs during render (two-phase pattern: derive effectiveBounds in render, persist in useLayoutEffect) */
import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { JUMP_TRACE } from "@/lib/debug-flags";
import { scrollLog } from "@/lib/scroll-profile";
import type { ScrollSnapshot } from "./use-scroll-anchor.js";

function jumpLog(label: string, detail?: Record<string, unknown>) {
  if (!JUMP_TRACE) return;
  const tail = detail
    ? " " +
      Object.entries(detail)
        .map(
          ([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`,
        )
        .join(" ")
    : "";
  console.debug(`[jump] ${label}${tail}`);
}

const DEFAULT_AVG_ROW_HEIGHT = 80;
// Signal-shape: small at-bottom window. Channel opens at
// INITIAL_WINDOW_SIZE and Phase 1 reconciliation pins it at
// `viewportRows + AT_BOTTOM_BUFFER` (floor MIN_WINDOW_SIZE) once the
// first slice settles — viewport-sized, not a wide multiple of it.
// Window grows beyond that ONLY when the user scrolls up (shift-up
// dispatch); scroll path remains tunable via SCROLLED_UP_MULTIPLIER
// and SHIFT_VIEWPORT_MULTIPLIER and is intentionally left wide so
// scrolling-up feels smooth. The data is in Zero's local IVM
// materialization regardless; this cap just bounds how many rows we
// ask React + the DOM to materialize per channel switch.
const INITIAL_WINDOW_SIZE = 15;
const MIN_WINDOW_SIZE = 30;
const AT_BOTTOM_BUFFER = 8;
const SCROLLED_UP_MULTIPLIER = 25;
// Per-dispatch growth amount for shift-up / shift-down. Smaller is
// better: each shift is reconciled by React synchronously, and adding
// 25× viewport rows in one dispatch (~250 rows on a typical viewport)
// is the long pole of the "older messages painting when I start the
// scroll" hitch. 5× viewport (~50 rows) keeps each shift's React work
// under one frame on typical hardware, while still extending the
// rendered slice ~4000px past the user's current scroll position —
// well beyond the 2000px trigger threshold, so the next shift only
// fires after the user has consumed the fresh buffer.
const SHIFT_VIEWPORT_MULTIPLIER = 5;
const OLDER_BIAS = 0.6;

// ─── Types ───────────────────────────────────────────────────────────

/**
 * All indices use half-open [startIndex, endIndex) convention matching
 * Array.slice(). This applies to: slicing, windowBoundsKey,
 * hasOlderInWindow (startIndex > 0), hasNewerInWindow (endIndex < len),
 * predecessorMessage (messages[startIndex - 1]),
 * beginning-of-conversation banner (startIndex === 0).
 */
export type WindowAction =
  | { type: "shift-up"; amount: number }
  | { type: "shift-down"; amount: number }
  | { type: "pin-bottom" }
  | { type: "focus-target"; targetIndex: number; windowSize?: number };

interface WindowBounds {
  startIndex: number;
  endIndex: number;
  isNearBottom: boolean;
}

interface SliceMeta {
  length: number;
  firstId: string | undefined;
  lastId: string | undefined;
}

interface UseMessageWindowOptions<T> {
  messages: readonly T[];
  viewportHeight: number;
  avgRowHeight?: number;
  /** For prepend reconciliation — reads the latest visible anchor on data changes */
  anchorSnapshotRef: RefObject<ScrollSnapshot | null>;
  /** For prepend reconciliation — determines whether to recenter or pin-to-end */
  isNearBottomRef: RefObject<boolean>;
  /**
   * Identity of the collection that `messages` belongs to (e.g. channelId).
   * When this changes, the hook resets bounds to pin-bottom of the new
   * collection instead of reusing old bounds. Without this, a single-mount
   * parent that swaps between collections can leave bounds pointing past
   * the end of a smaller new collection → window slice returns empty → a
   * blank list despite `messages` containing data.
   */
  resetKey?: string | null;
  /**
   * On resetKey change, seed the initial bounds around this message id
   * instead of pin-to-bottom. Used by channel-restore (saved scroll
   * anchor) and — eventually — unread-aware switching (oldest unread).
   *
   * When `messageId` is found in `messages`, the slice is sized via
   * `windowSize` (default: `computeMaxSize` for scrolled-up) and
   * centered around the anchor with the standard `OLDER_BIAS`. When
   * the id isn't in `messages` (cold channel, sync still loading),
   * falls back to the pin-to-bottom INITIAL_WINDOW_SIZE behavior so
   * the caller's scrollTarget machinery can take over on the slow
   * path.
   */
  initialAnchor?: { messageId: string; windowSize?: number } | null;
  /**
   * Suppress Phase 1 prepend-reconciliation while the ref reads true.
   *
   * Set by MessageListView during scroll-target navigation
   * (channel-switch-back to a saved anchor, notification jump,
   * in-app jump). The reconciliation otherwise re-derives bounds
   * each render based on `anchorSnapshotRef.current` — but during
   * a scroll-target transition the scroll handler is continuously
   * updating that ref as scrollTop animates toward the anchor,
   * which feeds back as bounds shifts that fight the dispatched
   * focus-target. Cleaner to stand down for the brief navigation
   * window and let the explicit dispatch own the bounds.
   *
   * After the flag clears (status="scrolled"), normal reconciliation
   * resumes — genuine prepends (older messages loaded) recenter
   * correctly. Ref-based so callers don't have to reorder state
   * declarations to satisfy the hook's prop dependency order.
   */
  suppressReconciliationRef?: RefObject<boolean>;
}

interface UseMessageWindowResult<T> {
  windowMessages: readonly T[];
  windowStartIndex: number;
  windowEndIndex: number;
  windowBoundsKey: string;
  hasOlderInWindow: boolean;
  hasNewerInWindow: boolean;
  predecessorMessage: T | null;
  dispatch: (action: WindowAction) => void;
  shiftWindowUp: () => void;
  shiftWindowDown: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────

// TEMP DIAGNOSTIC — remove once channel-switch perf work lands.
// Gated on `ano-cs-profile` localStorage flag so it never ships hot.
// Dedupes by (viewportRows + isNearBottom + result) to avoid spamming.
let lastComputeMaxSizeLog: string | null = null;
function logComputeMaxSize(
  viewportRows: number,
  isNearBottom: boolean,
  result: number,
) {
  if (typeof window === "undefined") return;
  if (!window.localStorage?.getItem("ano-cs-profile")) return;
  const key = `${viewportRows}|${isNearBottom}|${result}`;
  if (key === lastComputeMaxSizeLog) return;
  lastComputeMaxSizeLog = key;
  console.debug(
    `[cmx] viewportRows=${viewportRows} isNearBottom=${isNearBottom} → cap=${result}`,
  );
}

function computeMaxSize(viewportRows: number, isNearBottom: boolean): number {
  let result: number;
  if (isNearBottom) {
    // Pin-to-bottom: viewport + small buffer. Just enough rows to fill
    // the viewport with a few off-screen rows of headroom for any tiny
    // scroll. Keeps channel-switch paint cost predictable and tight.
    result = Math.max(viewportRows + AT_BOTTOM_BUFFER, MIN_WINDOW_SIZE);
  } else {
    // Scrolled up: keep the existing wide cap so scroll-up history loads
    // feel snappy. The user has explicitly asked for history, so paying
    // for more rows is fine here.
    result = Math.max(viewportRows * SCROLLED_UP_MULTIPLIER, MIN_WINDOW_SIZE);
  }
  logComputeMaxSize(viewportRows, isNearBottom, result);
  return result;
}

function getSliceMeta<T extends { id: string }>(
  messages: readonly T[],
): SliceMeta {
  return {
    length: messages.length,
    firstId: messages[0]?.id,
    lastId: messages[messages.length - 1]?.id,
  };
}

function sliceMetaEqual(a: SliceMeta, b: SliceMeta): boolean {
  return (
    a.length === b.length && a.firstId === b.firstId && a.lastId === b.lastId
  );
}

// ─── Hook ────────────────────────────────────────────────────────────

// TEMP DIAGNOSTIC — remove with the logComputeMaxSize helper above.
let lastViewportLog: string | null = null;
function logViewportInputs(
  viewportHeight: number,
  avgRowHeight: number,
  viewportRows: number,
) {
  if (typeof window === "undefined") return;
  if (!window.localStorage?.getItem("ano-cs-profile")) return;
  const key = `${viewportHeight}|${avgRowHeight}|${viewportRows}`;
  if (key === lastViewportLog) return;
  lastViewportLog = key;
  console.debug(
    `[cmx] viewportHeight=${viewportHeight} avgRowHeight=${avgRowHeight} → viewportRows=${viewportRows}`,
  );
}

export function useMessageWindow<T extends { id: string }>({
  messages,
  viewportHeight,
  avgRowHeight = DEFAULT_AVG_ROW_HEIGHT,
  anchorSnapshotRef,
  isNearBottomRef,
  resetKey = null,
  suppressReconciliationRef,
  initialAnchor = null,
}: UseMessageWindowOptions<T>): UseMessageWindowResult<T> {
  const viewportRows = Math.max(1, Math.ceil(viewportHeight / avgRowHeight));
  logViewportInputs(viewportHeight, avgRowHeight, viewportRows);

  // Stored bounds state — triggers re-render on dispatch.
  // Start with a small initial window for fast first paint, then expand.
  const [bounds, setBounds] = useState<WindowBounds>(() => ({
    startIndex: Math.max(0, messages.length - INITIAL_WINDOW_SIZE),
    endIndex: messages.length,
    isNearBottom: true,
  }));

  // Ref copy for external reads (sentinel callbacks, etc.)
  const boundsRef = useRef(bounds);
  const viewportRowsRef = useRef(viewportRows);
  const messagesLengthRef = useRef(messages.length);
  messagesLengthRef.current = messages.length;

  // Committed slice metadata — updated only in useLayoutEffect after commit.
  // Never mutated during render → safe under Strict Mode double-render.
  const committedSliceMetaRef = useRef<SliceMeta>(getSliceMeta(messages));
  const committedViewportRowsRef = useRef(viewportRows);
  const committedResetKeyRef = useRef<string | null>(resetKey);

  // ─── Phase 1: Derive effectiveBounds (pure, no ref mutations) ──

  const currentSliceMeta = getSliceMeta(messages);
  const sliceChanged = !sliceMetaEqual(
    currentSliceMeta,
    committedSliceMetaRef.current,
  );
  const viewportRowsChanged = viewportRows !== committedViewportRowsRef.current;
  // A resetKey change means the entire collection identity swapped under us
  // (e.g. channel switch while the parent fiber persists). Old bounds don't
  // translate — force a fresh pin-to-bottom window for the new collection.
  const resetKeyChanged = resetKey !== committedResetKeyRef.current;
  const needsReconciliation =
    sliceChanged || viewportRowsChanged || resetKeyChanged;

  let effectiveBounds: WindowBounds;

  if (!needsReconciliation) {
    effectiveBounds = bounds;
  } else if (resetKeyChanged) {
    // Channel just switched. Two paths:
    //
    // Warm/anchor path: caller provided `initialAnchor` AND the id is
    // in `messages` (cached channel with a known landing point — saved
    // scroll anchor for restore, or oldest unread for unread-aware
    // open). Seed the slice around it so the FIRST paint of the new
    // channel has the right rows. Combined with the matching cdu-side
    // scroll-offset apply in MessageScrollContainer, this eliminates
    // the one-frame flash of newest-messages on switch-back.
    //
    // Cold/default path: no initialAnchor, or the id isn't in
    // messages yet (sync still loading). Start at INITIAL_WINDOW_SIZE
    // pin-to-bottom for fast first paint; the caller's scrollTarget
    // useLayoutEffect takes over as soon as the id resolves.
    let anchorIdx = -1;
    if (initialAnchor) {
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].id === initialAnchor.messageId) {
          anchorIdx = i;
          break;
        }
      }
    }
    if (anchorIdx >= 0) {
      const maxSize =
        initialAnchor?.windowSize ?? computeMaxSize(viewportRows, false);
      const olderCount = Math.floor(maxSize * OLDER_BIAS);
      // newerCount must be ≥ viewportRows + buffer so that after the
      // cdu / layout-effect sets `scrollTop = naturalPos - savedOffset`,
      // the browser doesn't clamp scrollTop to maxScrollTop. If it
      // clamps, distToBot=0 and the next wheel-down attempt produces
      // NO scroll event (browser only fires scroll events when
      // scrollTop actually moves) → load-more handler never runs →
      // user is stuck. Enforced here AND in the focus-target reducer
      // because both code paths seed slices for channel-restore.
      const newerCount = Math.max(
        maxSize - olderCount,
        viewportRows + AT_BOTTOM_BUFFER,
      );
      const start = Math.max(0, anchorIdx - olderCount);
      const end = Math.min(messages.length, anchorIdx + newerCount);
      effectiveBounds = {
        startIndex: start,
        endIndex: end,
        isNearBottom: false,
      };
    } else {
      effectiveBounds = {
        startIndex: Math.max(0, messages.length - INITIAL_WINDOW_SIZE),
        endIndex: messages.length,
        isNearBottom: true,
      };
    }
  } else if (isNearBottomRef.current) {
    // Pin to end. Same "never shrink" protection as the pin-bottom
    // reducer — keeps the user's scrollback context intact when the
    // slice changes (e.g. new live message arrives) while they're at
    // the live tail with a previously-grown window.
    const currentWindowSize = bounds.endIndex - bounds.startIndex;
    const computedMaxSize = computeMaxSize(viewportRows, true);
    const maxSize = Math.max(computedMaxSize, currentWindowSize);
    effectiveBounds = {
      startIndex: Math.max(0, messages.length - maxSize),
      endIndex: messages.length,
      isNearBottom: true,
    };
  } else if (suppressReconciliationRef?.current) {
    // Scroll-target navigation in progress (channel-switch-back to a
    // saved anchor, notification jump, in-app jump). Keep bounds at
    // whatever the dispatched focus-target set them to; don't recenter
    // based on the scroll handler's transient anchorSnapshotRef
    // updates as scrollTop animates toward the target. After the
    // navigation completes the flag clears and reconciliation resumes.
    effectiveBounds = bounds;
  } else {
    // Scrolled up — find anchor in new array and recenter
    const anchorId = anchorSnapshotRef.current?.anchorMessageId;
    let anchorIndex = -1;

    if (anchorId) {
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].id === anchorId) {
          anchorIndex = i;
          break;
        }
      }
    }

    if (anchorIndex >= 0) {
      // Never shrink the window below its current size during
      // prepend reconciliation — shrinking unmounts messages the user
      // was viewing, loses anchors, and can cause snap-to-bottom on
      // the next data delivery.
      const currentWindowSize = bounds.endIndex - bounds.startIndex;
      const computedMaxSize = computeMaxSize(viewportRows, false);
      const maxSize = Math.max(computedMaxSize, currentWindowSize);
      const olderCount = Math.floor(maxSize * OLDER_BIAS);
      const start = Math.max(0, anchorIndex - olderCount);
      const end = Math.min(messages.length, start + maxSize);
      effectiveBounds = {
        startIndex: start,
        endIndex: end,
        isNearBottom: false,
      };
    } else {
      const currentWindowSize = bounds.endIndex - bounds.startIndex;
      if (messages.length < currentWindowSize) {
        // Transient shrink — dynamic query re-evaluating, fell back to
        // suspense query. Keep current bounds; full data arriving shortly.
        effectiveBounds = bounds;
      } else {
        // Genuine anchor loss (channel switch, message deleted).
        // Pin to bottom as safe default.
        const maxSize = computeMaxSize(viewportRows, true);
        effectiveBounds = {
          startIndex: Math.max(0, messages.length - maxSize),
          endIndex: messages.length,
          isNearBottom: true,
        };
      }
    }
  }

  // ─── Phase 2: Persist + update refs (useLayoutEffect) ──────────

  // Extract for eslint exhaustive-deps static analysis
  const messagesFirstId = messages[0]?.id;
  const messagesLastId = messages[messages.length - 1]?.id;

  useLayoutEffect(() => {
    committedSliceMetaRef.current = getSliceMeta(messages);
    committedViewportRowsRef.current = viewportRows;
    committedResetKeyRef.current = resetKey;
    boundsRef.current = effectiveBounds;
    viewportRowsRef.current = viewportRows;

    // Only call setBounds when bounds actually changed (structural equality)
    if (
      effectiveBounds.startIndex !== bounds.startIndex ||
      effectiveBounds.endIndex !== bounds.endIndex ||
      effectiveBounds.isNearBottom !== bounds.isNearBottom
    ) {
      setBounds(effectiveBounds);
    }
  }, [
    bounds,
    effectiveBounds,
    messages,
    // Include slice identity values so effect re-fires on fixed-length
    // cap rotations where length stays constant but content shifts.
    messages.length,
    messagesFirstId,
    messagesLastId,
    viewportRows,
    resetKey,
  ]);

  // ─── Dispatch ──────────────────────────────────────────────────

  const dispatch = useCallback(
    (action: WindowAction) => {
      setBounds((prev) => {
        // Use ref for current length — avoids stale closure if dispatch
        // is called from a rAF/observer callback between renders.
        const len = messagesLengthRef.current;
        switch (action.type) {
          case "shift-up": {
            const newStart = Math.max(0, prev.startIndex - action.amount);
            if (newStart === prev.startIndex) {
              scrollLog("dispatch shift-up noop", {
                start: prev.startIndex,
                reason: "at-zero",
              });
              return prev;
            }
            scrollLog("dispatch shift-up", {
              amount: action.amount,
              bounds: `${prev.startIndex}:${prev.endIndex}→${newStart}:${prev.endIndex}`,
              size: prev.endIndex - newStart,
            });
            return {
              startIndex: newStart,
              endIndex: prev.endIndex,
              isNearBottom: false,
            };
          }
          case "shift-down": {
            const newEnd = Math.min(len, prev.endIndex + action.amount);
            if (newEnd === prev.endIndex) {
              scrollLog("dispatch shift-down noop", {
                end: prev.endIndex,
                len,
                reason: "at-len",
              });
              return prev;
            }
            // Grow rightward only — never slide startIndex forward.
            // Sliding drops the rows the user is currently viewing and
            // produces disjoint windows whenever prev is small (e.g.
            // freshly seeded post channel-switch, size 30: amount=200
            // would put newStart = prev.endIndex = total teleport).
            // Mirrors the shift-up reducer which has no cap and only
            // grows leftward. Window grows monotonically during a
            // scroll session; trimming happens on channel switch
            // (resetKey reset).
            scrollLog("dispatch shift-down", {
              amount: action.amount,
              bounds: `${prev.startIndex}:${prev.endIndex}→${prev.startIndex}:${newEnd}`,
              size: newEnd - prev.startIndex,
            });
            return {
              startIndex: prev.startIndex,
              endIndex: newEnd,
              isNearBottom: false,
            };
          }
          case "pin-bottom": {
            // Preserve startIndex on transition — never shrink the
            // window when the user reaches the live tail. Shrinking
            // here collapses scrollHeight, browser clamps scrollTop,
            // and the next scroll triggers spurious shift-up because
            // we're suddenly "near top of slice." Window-shrinking
            // happens on channel-switch (resetKey reset), not here.
            scrollLog("dispatch pin-bottom", {
              bounds: `${prev.startIndex}:${prev.endIndex}→${prev.startIndex}:${len}`,
            });
            return {
              startIndex: prev.startIndex,
              endIndex: len,
              isNearBottom: true,
            };
          }
          case "focus-target": {
            // Single dispatch — sized window around the target. We
            // previously staged this via rAF (small slice first, then
            // expand) to chase faster first paint, but the second
            // setBounds gave Phase 1's prepend-reconciliation an extra
            // tick to fight the scroll-target intent and produced
            // cascading bounds shifts → visible jumps. One transition
            // is simpler.
            //
            // `windowSize` lets the caller request a tighter slice than
            // `computeMaxSize` would pick (which honors MIN_WINDOW_SIZE
            // = 150). Used by channel-restore navigation to render ~30
            // rows around the anchor on switch-back, where the full
            // scrolled-up window would pay the reconciliation cost of
            // ~150-200 React subtrees for no benefit — the user just
            // wants to land back where they were. Subsequent
            // shift-up / shift-down expand the slice normally.
            const maxSize =
              action.windowSize ??
              computeMaxSize(viewportRowsRef.current, false);
            const olderCount = Math.floor(maxSize * OLDER_BIAS);
            // See note in the initialAnchor seed above — newerCount is
            // floored at `viewportRows + AT_BOTTOM_BUFFER` so the
            // post-dispatch scrollTop apply has scroll-down headroom.
            const newerCount = Math.max(
              maxSize - olderCount,
              viewportRowsRef.current + AT_BOTTOM_BUFFER,
            );
            const start = Math.max(0, action.targetIndex - olderCount);
            const end = Math.min(len, action.targetIndex + newerCount);
            jumpLog("dispatch focus-target", {
              targetIndex: action.targetIndex,
              start,
              end,
              size: end - start,
            });
            return {
              startIndex: start,
              endIndex: end,
              isNearBottom: false,
            };
          }
        }
      });
    },
    // Stable: no deps on messages.length — uses ref instead
    [],
  );

  const shiftWindowUp = useCallback(() => {
    dispatch({
      type: "shift-up",
      amount: viewportRowsRef.current * SHIFT_VIEWPORT_MULTIPLIER,
    });
  }, [dispatch]);

  const shiftWindowDown = useCallback(() => {
    dispatch({
      type: "shift-down",
      amount: viewportRowsRef.current * SHIFT_VIEWPORT_MULTIPLIER,
    });
  }, [dispatch]);

  // ─── Derived values (all from effectiveBounds) ─────────────────

  const windowMessages = useMemo(
    () => messages.slice(effectiveBounds.startIndex, effectiveBounds.endIndex),
    [messages, effectiveBounds.startIndex, effectiveBounds.endIndex],
  );

  const predecessorMessage = useMemo(
    () =>
      effectiveBounds.startIndex > 0
        ? (messages[effectiveBounds.startIndex - 1] ?? null)
        : null,
    [messages, effectiveBounds.startIndex],
  );

  const windowBoundsKey = `${effectiveBounds.startIndex}:${effectiveBounds.endIndex}`;

  return {
    windowMessages,
    windowStartIndex: effectiveBounds.startIndex,
    windowEndIndex: effectiveBounds.endIndex,
    windowBoundsKey,
    hasOlderInWindow: effectiveBounds.startIndex > 0,
    hasNewerInWindow: effectiveBounds.endIndex < messages.length,
    predecessorMessage,
    dispatch,
    shiftWindowUp,
    shiftWindowDown,
  };
}
